import { useState } from 'react';
import ScrollReveal from '@/components/base/ScrollReveal';
import SectionHeading from '@/components/base/SectionHeading';

type FieldProps = {
  id: string;
  label: string;
  suffix: string;
  value: string;
  step?: string;
  onChange: (value: string) => void;
};

function NumberField({ id, label, suffix, value, step = '1', onChange }: FieldProps) {
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="text-xs font-medium text-foreground-600">
        {label}
      </label>
      <div className="mt-1.5 flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2 focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-100">
        <input
          id={id}
          type="number"
          min="0"
          step={step}
          inputMode="decimal"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="font-num w-full min-w-0 bg-transparent text-sm font-semibold text-foreground-950 outline-none"
        />
        <span className="shrink-0 text-xs text-foreground-500">{suffix}</span>
      </div>
    </div>
  );
}

function toNumber(value: string) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export default function PositionSizeCalculator() {
  const [balance, setBalance] = useState('2000');
  const [riskPercent, setRiskPercent] = useState('1');
  const [stopLoss, setStopLoss] = useState('25');
  const [pipValue, setPipValue] = useState('10');

  const riskAmount = (toNumber(balance) * toNumber(riskPercent)) / 100;
  const denominator = toNumber(stopLoss) * toNumber(pipValue);
  const rawLots = denominator > 0 ? riskAmount / denominator : 0;
  const lots = Math.max(0, Math.floor(rawLots * 100) / 100);
  const actualRisk = lots * denominator;
  const targetProfit = actualRisk * 2;

  return (
    <section className="bg-background-50">
      <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20 lg:px-10">
        <SectionHeading
          eyebrow="คำนวณก่อนกด"
          title="ขนาดล็อตคำนวณจากความเสี่ยง ไม่ใช่จากความมั่นใจ"
          subtitle="ลองใส่ตัวเลขของพอร์ตคุณดู แล้วจะเห็นว่าขนาดล็อตที่ปลอดภัยมักเล็กกว่าที่คิดไว้มาก"
        />

        <div className="mt-9 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1fr]">
          <ScrollReveal>
            <div className="rounded-xl border border-background-200 bg-background-100/60 p-5 md:p-6">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className="ri-equalizer-line text-sm" />
                </span>
                <h3 className="font-heading text-base font-semibold text-foreground-950">ข้อมูลของออเดอร์นี้</h3>
              </div>

              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <NumberField
                  id="calc-balance"
                  label="เงินทุนในพอร์ต"
                  suffix="USD"
                  step="50"
                  value={balance}
                  onChange={setBalance}
                />
                <NumberField
                  id="calc-risk"
                  label="ความเสี่ยงต่อออเดอร์"
                  suffix="%"
                  step="0.1"
                  value={riskPercent}
                  onChange={setRiskPercent}
                />
                <NumberField
                  id="calc-stop"
                  label="ระยะ Stop Loss"
                  suffix="pip"
                  value={stopLoss}
                  onChange={setStopLoss}
                />
                <NumberField
                  id="calc-pip"
                  label="มูลค่าต่อ pip ต่อ 1 ล็อต"
                  suffix="USD"
                  value={pipValue}
                  onChange={setPipValue}
                />
              </div>

              <div className="mt-5 rounded-lg border border-background-300 border-l-2 border-l-accent-500 bg-background-50 px-4 py-3">
                <p className="text-xs font-semibold text-foreground-800">สูตรที่ใช้</p>
                <p className="mt-1 font-num text-xs leading-relaxed text-foreground-600">
                  ล็อต = (เงินทุน × %ความเสี่ยง) ÷ (ระยะ Stop Loss × มูลค่าต่อ pip)
                </p>
              </div>
              <p className="mt-3 text-xs leading-relaxed text-foreground-500">
                ค่ามูลค่าต่อ pip ตัวอย่างใช้ 10 USD ซึ่งเป็นค่าของ EUR/USD ที่ 1 standard lot
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={90}>
            <div className="flex h-full flex-col rounded-xl border border-background-200 bg-background-100/60 p-5 md:p-6">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className="ri-calculator-line text-sm" />
                </span>
                <h3 className="font-heading text-base font-semibold text-foreground-950">ผลลัพธ์</h3>
              </div>

              <div className="mt-5 rounded-lg border border-primary-200 bg-primary-50 px-4 py-4">
                <p className="text-xs font-medium text-primary-700">ขนาดล็อตที่ควรใช้</p>
                <p className="mt-1 flex items-end gap-2">
                  <span className="font-num text-4xl font-semibold leading-none text-primary-800">
                    {lots.toFixed(2)}
                  </span>
                  <span className="pb-1 text-sm text-primary-700">lot</span>
                </p>
              </div>

              <dl className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                <div className="min-w-0 rounded-lg border border-background-200 bg-background-50 px-3.5 py-3">
                  <dt className="text-xs text-foreground-500">เงินที่ยอมเสี่ยง</dt>
                  <dd className="mt-0.5 font-num text-sm font-semibold text-foreground-950">
                    {riskAmount.toFixed(2)} USD
                  </dd>
                </div>
                <div className="min-w-0 rounded-lg border border-background-200 bg-background-50 px-3.5 py-3">
                  <dt className="text-xs text-foreground-500">ขาดทุนจริงถ้าโดน SL</dt>
                  <dd className="mt-0.5 font-num text-sm font-semibold text-accent-700">
                    -{actualRisk.toFixed(2)} USD
                  </dd>
                </div>
                <div className="col-span-1 min-w-0 rounded-lg border border-background-200 bg-background-50 px-3.5 py-3 sm:col-span-2">
                  <dt className="flex items-center justify-between gap-2 text-xs text-foreground-500">
                    กำไรที่เป้า 1 : 2
                    <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-900">
                      Risk / Reward
                    </span>
                  </dt>
                  <dd className="mt-0.5 font-num text-sm font-semibold text-secondary-900">
                    +{targetProfit.toFixed(2)} USD
                  </dd>
                </div>
              </dl>

              <p className="mt-auto pt-4 text-xs leading-relaxed text-foreground-500">
                ถ้าผลลัพธ์ออกมาไม่ถึง 0.01 lot แปลว่าพอร์ตยังเล็กเกินไปสำหรับระยะ Stop Loss นี้ ให้ลดความเสี่ยงหรือหาระยะที่แคบลง
              </p>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}